/**
 * Twin markers for a 2D twin map, clustered: markers that sit close together
 * merge into a count bubble, which lists its twins on hover and zooms in on a
 * click. Put it inside a `TurboTwinMap`. It needs the optional peer
 * `leaflet.markercluster`, and the host imports that plugin's CSS
 * (`leaflet.markercluster/dist/MarkerCluster.css`).
 *
 * A marker is `makeMarkerIcon` drawn in `colorOf(entry)`: its shape is the
 * twin's location mode, a thumbnail fills it, the selected twin is ringed, and
 * `labels` puts a name pill under each. A relationship `pick` rings the valid
 * targets and dims the rest. The selected twin, and a Dynamic twin that moved
 * in the last ten seconds, stay out of the clusters, so a live position or a
 * selection does not re-cluster the others; a Dynamic twin that went quiet
 * rejoins them.
 *
 * `interactive={false}` lets a click on a marker through to the map (a host
 * placing something on it, say); a cluster bubble still zooms in.
 *
 * Markers are added, moved and removed as `entries` changes, never torn down,
 * so a spiderfied cluster stays open through updates that add, move or remove
 * none of its markers. (markercluster closes it when one of them changes.) The ref
 * (`ClusteredMarkersHandle`) zooms to a twin, says where a twin shows right now
 * (for relationship lines) and pans it into view.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type ForwardedRef,
  type ReactElement,
  type RefAttributes,
} from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import { escapeHtml } from '../markerIcons';
import { diffPositions, type LatLngTuple } from '../markerDiff';
import type { RelPick } from '../relPick';
import { outsideView } from '../viewport';
import { setMarkerInteractive } from './markerInteraction';
import {
  MARKER_COLORS,
  makeMarkerIcon,
  type MarkerColors,
  type MarkerIconEntry,
  type MarkerPick,
} from './makeMarkerIcon';

/** A twin to mark. Any object with these fields works, and `colorOf` gets it back whole. */
export interface TwinMarkerEntry extends MarkerIconEntry {
  /** The twin's name; null shows its id. */
  displayName: string | null;
  /** Its model's name, on the marker's tooltip and in the cluster list. */
  modelName: string | null;
  /** Where it stands; a twin with no position gets no marker. */
  position: { latitude: number; longitude: number } | null;
}

/** The cluster bubble: its fill, its outline (also the head of the list a hover opens) and the count in it. */
export interface ClusterColors {
  background: string;
  accent: string;
  text: string;
}

/** The default cluster colours: a dark bubble with an orange outline. */
export const CLUSTER_COLORS: ClusterColors = {
  background: '#1a1a1a',
  accent: '#e07012',
  text: '#fafafa',
};

export interface ClusteredMarkersHandle {
  /** Zoom into the cluster to reveal the marker for a specific twin, then open its tooltip. */
  zoomToTwin: (twinId: string) => void;
  /** Where the twin shows right now: its marker, or the cluster bubble holding it. Null if it has no marker. */
  endpointOf: (twinId: string) => L.LatLng | null;
  /** Pan (zoom unchanged) to the twin's marker or cluster bubble when it is out of view; no marker, no move. */
  revealTwin: (twinId: string) => void;
}

export interface ClusteredMarkersLayerProps<E extends TwinMarkerEntry = TwinMarkerEntry> {
  /** The twins to mark. */
  entries: readonly E[];
  /**
   * A twin's marker colours, also its dot in the cluster list. CSS colours: they go into inline
   * styles as they are, so they must not come from user input. Default: `MARKER_COLORS` for every
   * marker. Keep the function stable (module level or `useCallback`): a new one restyles every marker.
   */
  colorOf?: (entry: E) => MarkerColors;
  /** The selected twin: ringed, above the others, and out of the clusters. */
  selectedTwinId?: string | null;
  /** A marker click. */
  onClick?: (twinId: string) => void;
  /**
   * The markers take clicks, right-clicks and hover. False lets them through to the map, with no
   * tooltip; a cluster bubble still lists its twins and zooms in. Default true.
   */
  interactive?: boolean;
  /** A marker right-click, at the pointer's client position (`x`, `y`). Without it the browser's own menu opens. */
  onContextMenu?: (twinId: string, x: number, y: number) => void;
  /** Clusters re-formed or spiderfied, a marker came or went, or the view moved: `endpointOf` answers may have changed. */
  onLayoutChange?: () => void;
  /** A relationship pick in progress (`relPickOf`): the `valid` twins ring green, the others dim except `keep`. Null: none. */
  pick?: RelPick | null;
  /** A name pill under every marker. Default false. */
  labels?: boolean;
  /** Cluster bubble colours; each one left out keeps its default (`CLUSTER_COLORS`). */
  colors?: Partial<ClusterColors>;
}

// Dynamic twins with no observed position change within this window are
// treated as parked and clustered like Static/None twins.
const DYNAMIC_MOVE_WINDOW_MS = 10_000;
const DYNAMIC_SWEEP_INTERVAL_MS = 2_000;

// What the layer keeps of each marker it made: its twin (the cluster list finds the twin's entry
// through it), the key of the icon last set (an unchanged icon is not set again) and the
// zIndexOffset last set (a spiderfy lifts its markers, and the layer must not undo that).
interface MarkerNote {
  twinId: string;
  icon?: string;
  z: number;
}
const NOTES = new WeakMap<L.Marker, MarkerNote>();

const defaultColorOf = () => MARKER_COLORS;

/** The twin markers as a clustered layer; the ref is the `ClusteredMarkersHandle`. */
export const ClusteredMarkersLayer = forwardRef(function ClusteredMarkersLayer<
  E extends TwinMarkerEntry,
>(
  {
    entries,
    colorOf = defaultColorOf,
    selectedTwinId = null,
    onClick,
    interactive = true,
    onContextMenu,
    onLayoutChange,
    pick = null,
    labels = false,
    colors,
  }: ClusteredMarkersLayerProps<E>,
  ref: ForwardedRef<ClusteredMarkersHandle>,
) {
  const map = useMap();
  const groupRef = useRef<L.MarkerClusterGroup | null>(null);
  const dynamicGroupRef = useRef<L.FeatureGroup | null>(null);
  const markerMapRef = useRef<Map<string, L.Marker>>(new Map());
  const entriesMapRef = useRef<Map<string, E>>(new Map());
  const prevSelectedIdRef = useRef<string | null>(null);
  // The position the layer last gave each marker. The diff compares with it, not with
  // getLatLng(): a spiderfied marker shows at its spider leg.
  const lastPosRef = useRef<Map<string, LatLngTuple>>(new Map());
  // Per-twin timestamp of last observed position change. A Dynamic twin is
  // considered "moving" only if it has reported a position change within
  // DYNAMIC_MOVE_WINDOW_MS; otherwise it clusters like Static/None twins.
  const lastMovedRef = useRef<Map<string, number>>(new Map());

  // Depends on the three colour strings, so an inline `colors` object restyles nothing.
  const clusterColors = useMemo<ClusterColors>(
    () => ({
      background: colors?.background ?? CLUSTER_COLORS.background,
      accent: colors?.accent ?? CLUSTER_COLORS.accent,
      text: colors?.text ?? CLUSTER_COLORS.text,
    }),
    [colors?.background, colors?.accent, colors?.text],
  );

  // A ref lets the mount-only setup and the marker-sync effect read the
  // latest values without listing them as deps (which would force the
  // cluster group to be torn down on every change). Set before the effects below run.
  const latest = useRef({
    colorOf,
    onClick,
    interactive,
    onContextMenu,
    onLayoutChange,
    pick,
    labels,
    clusterColors,
  });
  useEffect(() => {
    latest.current = {
      colorOf,
      onClick,
      interactive,
      onContextMenu,
      onLayoutChange,
      pick,
      labels,
      clusterColors,
    };
  });

  // Cluster bubbles are drawn once per cluster: draw them again when a colour changes.
  useEffect(() => {
    groupRef.current?.refreshClusters();
  }, [clusterColors]);

  // Every marker icon goes through here, so selection, colours and pick state agree. An unchanged
  // icon is not set again: the entries and the pick are rebuilt on every data update (live
  // positions included), and a setIcon rewrites the icon HTML under the pointer, which loses the
  // click that picks a target.
  const applyIcon = useCallback((marker: L.Marker, entry: E) => {
    const p = latest.current.pick;
    const pickState: MarkerPick | undefined = !p
      ? undefined
      : p.valid.has(entry.twinId)
        ? 'valid'
        : entry.twinId === p.keep
          ? undefined
          : 'invalid';
    const selected = entry.twinId === prevSelectedIdRef.current;
    // Leaflet stacks markers by latitude: lift the selected one above every marker and cluster bubble.
    const z = selected ? 100_000 : 0;
    const note = NOTES.get(marker);
    if (note && note.z !== z) {
      note.z = z;
      marker.setZIndexOffset(z);
    }
    const markerColors = latest.current.colorOf(entry);
    const { thumbnailUrl, locationMode } = entry;
    const label = latest.current.labels ? (entry.displayName ?? entry.twinId) : undefined;
    const key = [
      selected,
      markerColors.fill,
      markerColors.stroke,
      pickState,
      thumbnailUrl,
      locationMode,
      label,
    ].join('|');
    if (note?.icon === key) return;
    if (note) note.icon = key;
    marker.setIcon(makeMarkerIcon(entry, markerColors, { selected, pick: pickState, label }));
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      zoomToTwin(twinId: string) {
        const group = groupRef.current;
        const marker = markerMapRef.current.get(twinId);
        if (!group || !marker) return;
        // A camera command reaches a just-raised tab as soon as it measures
        // visible, a frame before the map's resize hook runs. Re-read the size,
        // or flyTo divides by the 0x0 cached while hidden and throws on NaN.
        map.invalidateSize();
        const size = map.getSize();
        if (size.x === 0 || size.y === 0) return;
        const openMarkerTooltip = () => {
          markerMapRef.current.forEach((m, id) => {
            if (id !== twinId) m.closeTooltip();
          });
          marker.openTooltip();
        };
        // zoomToShowLayer only pans or expands clusters: a marker already drawn on
        // screen got no zoom at all. Fly in when the marker stands on its own. Moving Dynamic markers and the selected
        // twin live outside the cluster group, so zoomToShowLayer never applies to them.
        if (!group.hasLayer(marker) || group.getVisibleParent(marker) === marker) {
          map.flyTo(marker.getLatLng(), Math.max(map.getZoom(), 17), { duration: 0.8 });
          setTimeout(openMarkerTooltip, 900);
        } else {
          group.zoomToShowLayer(marker, openMarkerTooltip);
        }
      },
      endpointOf(twinId: string) {
        const marker = markerMapRef.current.get(twinId);
        const group = groupRef.current;
        if (!marker) return null;
        // Moving Dynamic markers sit outside the group. Off-screen, nothing holding the marker is drawn and
        // getVisibleParent is null; the marker's own position is right for both.
        if (!group?.hasLayer(marker)) return marker.getLatLng();
        return (group.getVisibleParent(marker) ?? marker).getLatLng();
      },
      revealTwin(twinId: string) {
        const marker = markerMapRef.current.get(twinId);
        if (!marker) return;
        map.invalidateSize(); // a just-shown tab still caches its hidden 0x0 size
        const size = map.getSize();
        if (size.x === 0 || size.y === 0) return;
        // Off screen the bubble holding it is not drawn (getVisibleParent null): the marker's own spot.
        const group = groupRef.current;
        const at = (
          (group?.hasLayer(marker) && group.getVisibleParent(marker)) ||
          marker
        ).getLatLng();
        const p = map.latLngToContainerPoint(at);
        if (outsideView(p.x, p.y, size.x, size.y)) map.panTo(at);
      },
    }),
    [map],
  );

  // Mount cluster group + dynamic-marker featureGroup once per map mount.
  // Marker membership is maintained by the diff-based sync effect below so
  // live entry updates don't tear down spiderfy state.
  useEffect(() => {
    const markerMap = markerMapRef.current;
    const entriesMap = entriesMapRef.current;
    const lastMoved = lastMovedRef.current;
    const lastPos = lastPosRef.current;

    const group = L.markerClusterGroup({
      // Tight 1px radius at max zoom keeps clustering for genuine
      // same-position overlaps (so stacked twins stay selectable via
      // spiderfy) but lets adjacent-but-distinct twins separate visually.
      maxClusterRadius: (zoom: number) => (zoom >= 19 ? 1 : 40),
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      chunkedLoading: true,
      iconCreateFunction: (cluster) => {
        const { background, accent, text } = latest.current.clusterColors;
        const count: number = cluster.getChildCount();
        const size = count < 10 ? 32 : count < 100 ? 38 : 44;
        const fontSize = count < 100 ? 13 : 11;
        return L.divIcon({
          className: '',
          html: `<div style="
              width:${size}px;height:${size}px;border-radius:50%;
              background:${background};
              border:2px solid ${accent};
              box-shadow:0 2px 8px rgba(0,0,0,0.5),0 0 0 1px rgba(0,0,0,0.25);
              display:flex;align-items:center;justify-content:center;
              font-weight:700;font-size:${fontSize}px;color:${text};
              font-family:system-ui,sans-serif;line-height:1;
            ">${count}</div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
      },
    });

    group.on('clustermouseover', (e) => {
      const cluster = e.layer as L.MarkerCluster;
      const children = cluster.getAllChildMarkers();
      const LIMIT = 10;
      const shown = children.slice(0, LIMIT);
      const extra = children.length - shown.length;

      const rows = shown
        .map((m) => {
          const twinId = NOTES.get(m)?.twinId;
          const d = twinId === undefined ? undefined : entriesMapRef.current.get(twinId);
          if (!d) return '';
          const color = latest.current.colorOf(d).fill;
          const model = d.modelName
            ? `<span style="color:#aaa;font-size:10px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:90px;display:inline-block;vertical-align:middle">${escapeHtml(d.modelName)}</span>`
            : '';
          return `<div style="display:flex;align-items:center;gap:6px;padding:2px 10px">
            <span style="flex-shrink:0;width:8px;height:8px;border-radius:50%;background:${color};display:inline-block"></span>
            <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;font-size:11.5px" title="${escapeHtml(d.twinId)}">${escapeHtml(d.displayName ?? d.twinId)}</span>
            ${model}
          </div>`;
        })
        .join('');

      const footer =
        extra > 0
          ? `<div style="padding:3px 10px 2px;color:#aaa;font-size:10px;border-top:1px solid rgba(255,255,255,0.08);text-align:center">+${extra} more</div>`
          : '';

      const html = `<div style="min-width:210px;max-width:300px;font-family:system-ui,sans-serif;overflow:hidden;border-radius:6px">
          <div style="background:${latest.current.clusterColors.accent};color:#fff;padding:5px 10px;display:flex;justify-content:space-between;align-items:center">
            <span style="font-weight:600;font-size:12px">Cluster</span>
            <span style="background:rgba(255,255,255,0.2);border-radius:10px;padding:1px 8px;font-size:11px;font-weight:600">${children.length} twins</span>
          </div>
          <div style="padding:4px 0">${rows}${footer}</div>
          <div style="padding:3px 10px 5px;color:#aaa;font-size:10px;border-top:1px solid rgba(255,255,255,0.08);text-align:center">Click to zoom in</div>
        </div>`;

      cluster.bindTooltip(html, { direction: 'top', sticky: false, opacity: 1 });
      cluster.openTooltip();
    });
    group.on('clustermouseout', (e) => {
      e.layer.closeTooltip();
    });
    // Registered after the group's own moveend handler, so clusters are settled when it fires.
    const layoutChanged = () => latest.current.onLayoutChange?.();
    group.on('animationend spiderfied unspiderfied', layoutChanged);
    map.on('moveend', layoutChanged);

    const dynamicGroup = L.featureGroup();

    map.addLayer(group);
    map.addLayer(dynamicGroup);
    groupRef.current = group;
    dynamicGroupRef.current = dynamicGroup;

    // Sweep: move Dynamic markers back into the cluster group once their
    // last position change ages past DYNAMIC_MOVE_WINDOW_MS. Without this,
    // a vehicle that goes silent stays in the dynamic featureGroup
    // forever; with it, parked vehicles rejoin clustering after 10s of
    // quiet.
    const sweep = setInterval(() => {
      const now = Date.now();
      for (const [twinId, marker] of markerMap) {
        const entry = entriesMap.get(twinId);
        if (!entry || entry.locationMode !== 'Dynamic') continue;
        const moving =
          twinId === prevSelectedIdRef.current ||
          now - (lastMovedRef.current.get(twinId) ?? 0) < DYNAMIC_MOVE_WINDOW_MS;
        const inDynamic = dynamicGroup.hasLayer(marker);
        if (!moving && inDynamic) {
          dynamicGroup.removeLayer(marker);
          group.addLayer(marker);
        } else if (moving && !inDynamic && group.hasLayer(marker)) {
          group.removeLayer(marker);
          dynamicGroup.addLayer(marker);
        }
      }
    }, DYNAMIC_SWEEP_INTERVAL_MS);

    return () => {
      clearInterval(sweep);
      map.off('moveend', layoutChanged);
      map.removeLayer(group);
      map.removeLayer(dynamicGroup);
      groupRef.current = null;
      dynamicGroupRef.current = null;
      markerMap.clear();
      entriesMap.clear();
      lastMoved.clear();
      lastPos.clear();
    };
  }, [map]);

  // Diff-based sync: add new markers, remove gone ones, update positions
  // on existing. Static/None twins (and idle Dynamic twins) land in the
  // cluster group; actively moving Dynamic twins land in the dynamic
  // featureGroup so their per-update setLatLng calls don't churn the
  // cluster. Idle/moving transitions also happen on a sweep interval
  // (see mount effect) so a vehicle that stops moving rejoins clustering
  // without waiting for its next position update.
  useEffect(() => {
    const group = groupRef.current;
    const dynamicGroup = dynamicGroupRef.current;
    if (!group || !dynamicGroup) return;

    const now = Date.now();
    let changed = false;

    const positions = new Map<string, LatLngTuple>();
    for (const entry of entries) {
      if (!entry.position) continue;
      entriesMapRef.current.set(entry.twinId, entry);
      positions.set(entry.twinId, [entry.position.latitude, entry.position.longitude]);
    }
    // Only call setLatLng when the position actually changed. An
    // unconditional setLatLng fires a 'move' event even with identical
    // coordinates, which the cluster plugin treats as a real move and
    // re-evaluates clustering, collapsing any active spiderfy.
    const { moved, removed } = diffPositions(lastPosRef.current, positions);
    const movedIds = new Set(moved);

    for (const entry of entries) {
      if (!entry.position) continue;
      const latLng: [number, number] = [entry.position.latitude, entry.position.longitude];
      const existing = markerMapRef.current.get(entry.twinId);

      if (existing) {
        if (movedIds.has(entry.twinId)) {
          existing.setLatLng(latLng);
          if (entry.locationMode === 'Dynamic') {
            lastMovedRef.current.set(entry.twinId, now);
          }
        }
        // The selected twin stays out of clusters too, like a moving one.
        const moving =
          entry.twinId === prevSelectedIdRef.current ||
          (entry.locationMode === 'Dynamic' &&
            now - (lastMovedRef.current.get(entry.twinId) ?? 0) < DYNAMIC_MOVE_WINDOW_MS);
        const targetGroup: L.FeatureGroup = moving ? dynamicGroup : group;
        const otherGroup: L.FeatureGroup = moving ? group : dynamicGroup;
        const groupSwapped = otherGroup.hasLayer(existing);
        // Re-render the icon on a group swap; applyIcon catches a thumbnail
        // that resolves later (the host loads it after the marker).
        if (groupSwapped) {
          otherGroup.removeLayer(existing);
          targetGroup.addLayer(existing);
          const note = NOTES.get(existing);
          if (note) note.icon = undefined;
        }
        applyIcon(existing, entry);
      } else {
        // New marker: no prior motion observed, treat as idle and cluster.
        const twinId = entry.twinId;
        const marker = L.marker(latLng, { interactive: latest.current.interactive });
        NOTES.set(marker, { twinId, z: 0 }); // a new marker's zIndexOffset is 0
        applyIcon(marker, entry);
        const tooltip = [
          `<strong>${escapeHtml(entry.displayName ?? twinId)}</strong>`,
          entry.displayName ? `<span style="opacity:0.7">${escapeHtml(twinId)}</span>` : null,
          entry.modelName ? escapeHtml(entry.modelName) : null,
          `<em>${escapeHtml(entry.locationMode)}</em>`,
        ]
          .filter(Boolean)
          .join('<br/>');
        marker.bindTooltip(tooltip, { direction: 'top', offset: [0, -8] });
        marker.on('click', () => latest.current.onClick?.(twinId));
        marker.on('contextmenu', (e: L.LeafletMouseEvent) => {
          // No handler: leave the browser's own menu.
          const handler = latest.current.onContextMenu;
          if (!handler) return;
          L.DomEvent.preventDefault(e.originalEvent);
          handler(twinId, e.originalEvent.clientX, e.originalEvent.clientY);
        });
        (twinId === prevSelectedIdRef.current ? dynamicGroup : group).addLayer(marker);
        markerMapRef.current.set(twinId, marker);
        changed = true;
      }
    }

    for (const twinId of removed) {
      const marker = markerMapRef.current.get(twinId);
      if (!marker) continue;
      group.removeLayer(marker);
      dynamicGroup.removeLayer(marker);
      markerMapRef.current.delete(twinId);
      entriesMapRef.current.delete(twinId);
      lastMovedRef.current.delete(twinId);
      changed = true;
    }
    // A marker came or went (an editor takes a twin out and puts it back) with
    // nothing else changing: `endpointOf` answers differently, so a host's
    // relationship lines have to redraw.
    if (changed) latest.current.onLayoutChange?.();
  }, [entries, applyIcon]);

  // Update only the two affected markers when selection changes, without tearing
  // down the cluster group: the selected twin leaves its cluster (unclustered
  // featureGroup), the deselected one rejoins it unless it is still moving.
  useEffect(() => {
    const prev = prevSelectedIdRef.current;
    prevSelectedIdRef.current = selectedTwinId;
    const group = groupRef.current;
    const dynamicGroup = dynamicGroupRef.current;
    let changed = false;

    if (prev) {
      const marker = markerMapRef.current.get(prev);
      const entry = entriesMapRef.current.get(prev);
      if (marker && entry) {
        const moving =
          entry.locationMode === 'Dynamic' &&
          Date.now() - (lastMovedRef.current.get(prev) ?? 0) < DYNAMIC_MOVE_WINDOW_MS;
        if (prev !== selectedTwinId && !moving && group && dynamicGroup?.hasLayer(marker)) {
          dynamicGroup.removeLayer(marker);
          group.addLayer(marker);
          changed = true;
        }
        applyIcon(marker, entry);
      }
    }
    if (selectedTwinId) {
      const marker = markerMapRef.current.get(selectedTwinId);
      const entry = entriesMapRef.current.get(selectedTwinId);
      if (marker && entry) {
        if (dynamicGroup && group?.hasLayer(marker)) {
          group.removeLayer(marker);
          dynamicGroup.addLayer(marker);
          changed = true;
        }
        applyIcon(marker, entry);
      }
    }
    if (changed) latest.current.onLayoutChange?.();
  }, [selectedTwinId, colorOf, applyIcon]);

  // Switch every marker's clicks in place: re-adding them would re-cluster and close a spider.
  useEffect(() => {
    markerMapRef.current.forEach((marker) => setMarkerInteractive(marker, interactive));
  }, [interactive]);

  // Restyle every marker in place when the colours, the pick or the labels toggle. Avoids
  // tearing down the cluster group (which would lose expansion state).
  useEffect(() => {
    markerMapRef.current.forEach((marker, twinId) => {
      const entry = entriesMapRef.current.get(twinId);
      if (entry) applyIcon(marker, entry);
    });
  }, [applyIcon, colorOf, pick, labels]);

  return null;
}) as <E extends TwinMarkerEntry>(
  props: ClusteredMarkersLayerProps<E> & RefAttributes<ClusteredMarkersHandle>,
) => ReactElement | null;
